import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, Check, Trash2, Info, AlertTriangle, CheckCircle2, Layers } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, type NotificationCategory, type NotificationItem } from '../../context/NotificationContext';
import { timeAgo } from '../../lib/utils';

type FilterTab = 'all' | NotificationCategory | 'erreur';

const TABS: { id: FilterTab; label: string }[] = [
    { id: 'all',     label: 'Tout' },
    { id: 'analyse', label: 'Analyses' },
    { id: 'mission', label: 'Missions' },
    { id: 'erreur',  label: 'Erreurs' },
];

export const NotificationCenter = () => {
    const { notifications, unreadCount, markAllAsRead, clearAll, markAsRead, removeNotification } = useNotifications();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
    const panelRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Filter notifications by active tab
    const filteredNotifications = notifications.filter(n => {
        if (activeFilter === 'all')    return true;
        if (activeFilter === 'erreur') return n.type === 'error';
        return n.category === activeFilter;
    });

    // Sort: unread errors first, then by recency
    const sortedNotifications = [...filteredNotifications].sort((a, b) => {
        const aIsUrgent = !a.read && a.type === 'error';
        const bIsUrgent = !b.read && b.type === 'error';
        if (aIsUrgent && !bIsUrgent) return -1;
        if (!aIsUrgent && bIsUrgent) return 1;
        return b.timestamp - a.timestamp;
    });

    // Badge counts per tab
    const errorUnread   = notifications.filter(n => n.type === 'error' && !n.read).length;
    const analyseUnread = notifications.filter(n => n.category === 'analyse' && !n.read).length;
    const missionUnread = notifications.filter(n => n.category === 'mission' && !n.read).length;

    const tabUnread: Record<FilterTab, number> = {
        all:     unreadCount,
        analyse: analyseUnread,
        mission: missionUnread,
        erreur:  errorUnread,
        systeme: 0,
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />;
            case 'error':   return <AlertTriangle size={15} className="text-red-500 shrink-0" />;
            case 'warning': return <AlertTriangle size={15} className="text-amber-500 shrink-0" />;
            default:        return <Info size={15} className="text-blue-500 shrink-0" />;
        }
    };

    const getBgColor = (type: string, read: boolean) => {
        if (read) return 'bg-white border-slate-100';
        switch (type) {
            case 'success': return 'bg-emerald-50 border-emerald-100';
            case 'error':   return 'bg-red-50 border-red-200';
            case 'warning': return 'bg-amber-50 border-amber-100';
            default:        return 'bg-blue-50 border-blue-100';
        }
    };

    /** Absolute date for tooltip — shown on hover over relative timestamp */
    const absoluteDate = (ts: number) =>
        new Date(ts).toLocaleString('fr-FR', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });

    return (
        <div className="relative">
            {/* Trigger Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                title="Notifications"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center leading-none border-2 border-slate-900">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Slide-over Panel */}
            {isOpen && (
                <div
                    ref={panelRef}
                    className="absolute bottom-full left-0 mb-2 w-80 md:w-[22rem] bg-white rounded-xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 z-50 origin-bottom-left"
                    style={{ bottom: '100%', left: '0' }}
                >
                    {/* Header */}
                    <div className="px-4 pt-4 pb-3 border-b border-slate-100 bg-slate-50/80 backdrop-blur-sm">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <h3 className="font-bold text-slate-900 text-sm">Notifications</h3>
                                {unreadCount > 0 && (
                                    <span className="px-2 py-0.5 bg-red-100 text-red-600 text-[11px] font-bold rounded-full">
                                        {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={markAllAsRead}
                                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                        title="Tout marquer comme lu"
                                    >
                                        <Check size={15} />
                                    </button>
                                )}
                                <button
                                    onClick={clearAll}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Effacer tout"
                                >
                                    <Trash2 size={15} />
                                </button>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                                >
                                    <X size={15} />
                                </button>
                            </div>
                        </div>

                        {/* Filter tabs */}
                        <div className="flex gap-1">
                            {TABS.map(tab => {
                                const count = tabUnread[tab.id];
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveFilter(tab.id)}
                                        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                                            activeFilter === tab.id
                                                ? tab.id === 'erreur'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-slate-200 text-slate-800'
                                                : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                                        }`}
                                    >
                                        {tab.label}
                                        {count > 0 && (
                                            <span className={`min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center leading-none ${
                                                tab.id === 'erreur'
                                                    ? 'bg-red-500 text-white'
                                                    : 'bg-slate-400 text-white'
                                            }`}>
                                                {count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-1.5 max-h-[400px]">
                        {sortedNotifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                                {activeFilter === 'all' ? (
                                    <>
                                        <Bell size={28} className="opacity-20 mb-2" />
                                        <p className="text-xs">Aucune notification</p>
                                    </>
                                ) : (
                                    <>
                                        <Layers size={28} className="opacity-20 mb-2" />
                                        <p className="text-xs">Aucune notification dans cette catégorie</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            sortedNotifications.map(item => (
                                <div
                                    key={item.id}
                                    className={`relative group p-3 rounded-lg border transition-all cursor-pointer ${getBgColor(item.type, item.read)} ${!item.read ? 'ring-1 ring-inset ring-blue-300/40' : ''}`}
                                    onClick={() => markAsRead(item.id)}
                                >
                                    {/* Unread dot */}
                                    {!item.read && (
                                        <span className="absolute top-3 right-3 w-1.5 h-1.5 rounded-full bg-blue-500" />
                                    )}

                                    <div className="flex items-start gap-2.5">
                                        <div className="mt-0.5">
                                            {getIcon(item.type)}
                                        </div>
                                        <div className="flex-1 min-w-0 pr-3">
                                            <div className="flex items-baseline justify-between gap-2 mb-0.5">
                                                <h4 className={`text-xs font-semibold leading-tight ${item.read ? 'text-slate-600' : 'text-slate-900'}`}>
                                                    {item.title}
                                                </h4>
                                                {/* Timestamp with absolute date tooltip */}
                                                <span
                                                    className="text-[10px] text-slate-400 whitespace-nowrap shrink-0 cursor-help"
                                                    title={absoluteDate(item.timestamp)}
                                                >
                                                    {timeAgo(item.timestamp)}
                                                </span>
                                            </div>
                                            {item.message && (
                                                <p className="text-[11px] text-slate-500 leading-relaxed break-words mt-0.5">
                                                    {item.message}
                                                </p>
                                            )}
                                            {item.action && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        markAsRead(item.id);
                                                        if (item.action?.onClick) item.action.onClick();
                                                        else if (item.action?.path) navigate(item.action.path);
                                                        setIsOpen(false);
                                                    }}
                                                    className="mt-1.5 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-0.5"
                                                >
                                                    {item.action.label} <span className="text-[9px]">→</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Remove button — visible on hover */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeNotification(item.id);
                                        }}
                                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-red-500 transition-opacity rounded"
                                        title="Supprimer"
                                    >
                                        <X size={11} />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
